import { graphql, useStaticQuery } from 'gatsby';
import { sortBy } from 'lodash';
import { DateTime } from 'luxon';
import { useCallback } from 'react';
import { useMappedValue } from '../../hooks/useMappedHook';
import {
  MonitoredBlogWithStats,
  RankingData,
  RankingDataMetaStats,
  STATS_RANGE,
  TopicStats
} from '../../types/Ranking';
import { SubmissionTopic } from '../../types/Submission';

const EMPTY_TOPIC_STATS = (topic: SubmissionTopic | 'OVERALL'): TopicStats => {
  return {
    topic,
    scoreLast30Days: { curr: 0, prev: 0 },
    rankLast30Days: { curr: 0, prev: 0 },
    rankTrend: Array(STATS_RANGE / 2).fill(0),
    scoreTrend: Array(STATS_RANGE).fill(0),
    scoresByDay: Array(STATS_RANGE).fill(0)
  };
};

let parsedRankingData: RankingData;
const useRankingData = (): RankingData => {
  const {
    allRankingsJson: { edges }
  } = useStaticQuery(graphql`
    query {
      allRankingsJson {
        edges {
          node {
            data
          }
        }
      }
    }
  `);
  if (!parsedRankingData) {
    parsedRankingData = JSON.parse(edges[0].node.data);
  }
  return parsedRankingData;
};

export const getScoreForLastNDays = <T extends { scoresByDay: number[] }>(
  d: T,
  days: number
) => {
  return d.scoresByDay.slice(-days).reduce((m, s) => m + s, 0);
};

export const useBlogsForTopic = (topic: SubmissionTopic | 'OVERALL') => {
  const data = useRankingData();
  return topic === 'OVERALL'
    ? data.blogs
    : data.blogs.filter((b) => b.topics.includes(topic));
};

export type Mode = 'all' | 'new';
export const NEW_BLOG_CUTOFF_DAYS = 10;
const NEW_BLOG_CUTOFF = DateTime.local()
  .minus({ days: NEW_BLOG_CUTOFF_DAYS })
  .toMillis();

export const useBlogsForTopicSortedByLastNDays = (
  topic: SubmissionTopic | 'OVERALL',
  mode: Mode,
  days: number,
  maxItems = 45
) => {
  const mapFn = useCallback(
    (bs: MonitoredBlogWithStats[]) => {
      return sortBy(
        mode === 'all' ? bs : bs.filter((b) => b.createdAt > NEW_BLOG_CUTOFF),
        (b) => -getScoreForLastNDays(b.stats, days)
      ).slice(0, maxItems);
    },
    [mode, days, maxItems]
  );
  return useMappedValue(useBlogsForTopic(topic), mapFn, true);
};

export const getTopPageSortedByLastNDays = (
  blog: MonitoredBlogWithStats,
  days: number
) => {
  return sortBy(blog.stats.topPages, (p) => -getScoreForLastNDays(p, days));
};

const getTopicStats = (
  data: RankingData,
  topic: SubmissionTopic | 'OVERALL'
) => {
  return data.statsByTopic[topic] || EMPTY_TOPIC_STATS(topic);
};

export const useTopicStats = (topic: SubmissionTopic | 'OVERALL') => {
  const data = useRankingData();
  return getTopicStats(data, topic);
};

export const useAllTopicStats = (topics: SubmissionTopic[]) => {
  const data = useRankingData();
  return topics.map((t) => getTopicStats(data, t));
};

export const useMetaStats = (): RankingDataMetaStats => {
  const data = useRankingData();
  return data.metaStats;
};
